import { Badge, Button, Card, EmptyState, Group, Stack, Switch, Table, Text, ThemeIcon } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useAckEvent, useConfirmMetadata, useEvents } from "../../api/generated/stockvol";
import type { PageEventOut } from "../../api/generated/model";
import { useSiteInfo } from "../../api/hooks";
import { RequireRole } from "../../auth";
import { act, confirm } from "../../lib/actions";
import { IconAlertOctagon, IconAlertTriangle, IconCircleCheck, IconInfoCircle } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { TableCard } from "../../components/Section";
import { TableSkeleton } from "../../components/Skeletons";
import { EVENT_TEXT } from "../../lib/eventText";
import { formatLocal } from "../../lib/time";

const SEV: Record<string, string> = { alarm: "red", warning: "yellow", info: "gray" };
const SEV_ICON = { alarm: IconAlertOctagon, warning: IconAlertTriangle, info: IconInfoCircle } as const;

/** A payload as small key: value chips (objects shortened). */
function PayloadChips({ payload }: { payload: Record<string, unknown> | null | undefined }) {
  const entries = Object.entries(payload ?? {});
  if (entries.length === 0) return null;
  return (
    <Group gap={4} wrap="wrap">
      {entries.slice(0, 6).map(([k, v]) => {
        const text = typeof v === "object" ? JSON.stringify(v) : String(v);
        return (
          <Badge key={k} size="sm" variant="default" tt="none" fw={500} maw={260} title={`${k}: ${text}`}>
            <Text span size="xs" c="dimmed">
              {k}
            </Text>{" "}
            {text.length > 28 ? `${text.slice(0, 28)}…` : text}
          </Badge>
        );
      })}
      {entries.length > 6 && (
        <Text size="xs" c="dimmed">
          +{entries.length - 6} more
        </Text>
      )}
    </Group>
  );
}
/** Events whose payload names the sensor's new metadata: an admin accepts it (M7.5 C11). */
const METADATA_KINDS = new Set(["skipped_metadata_changed", "sensor_metadata_changed"]);
const data = <T,>(r: unknown) => (r as { data: T }).data;

/** Events, newest first; operators acknowledge them (M6.6). */
export default function EventsPage() {
  const site = useSiteInfo();
  const tz = site.data?.timezone ?? "UTC";
  const [openOnly, setOpenOnly] = useState(true);
  const events = useEvents(
    { open_only: openOnly, limit: 200 },
    { query: { select: data<PageEventOut>, refetchInterval: 15_000 } },
  );
  const qc = useQueryClient();
  const ack = useAckEvent();
  const accept = useConfirmMetadata();
  const acceptMetadata = (sha: string, stacker: string | null | undefined) =>
    confirm(
      "Accept the sensor's new metadata",
      `Ingest records again with metadata ${sha.slice(0, 12)}…. A new firmware or a replaced sensor can change the beam angles: check the next sweep.`,
      () =>
        void act(() => accept.mutateAsync({ data: { sha256: sha, stacker: stacker ?? null } }), {
          done: "New sensor metadata accepted",
          qc,
          refresh: ["events", "status"],
        }),
    );
  const items = events.data?.items ?? [];
  const open = items.filter((e) => !e.acknowledged_at);
  const alarms = open.filter((e) => e.severity === "alarm").length;
  return (
    <Stack gap="lg">
      <PageHeader
        title="Events"
        subtitle={
          events.isPending
            ? undefined
            : `${open.length} open${alarms ? ` · ${alarms} ${alarms === 1 ? "alarm" : "alarms"}` : ""}${openOnly ? "" : ` · ${items.length} shown`}`
        }
        actions={
          <Switch label="Open only" checked={openOnly} onChange={(e) => setOpenOnly(e.currentTarget.checked)} />
        }
      />
      {events.isPending ? (
        <TableSkeleton rows={8} cols={4} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconCircleCheck size={30} />}
            title={openOnly ? "All clear" : "Nothing recorded"}
            description={openOnly ? "Every event has been acknowledged." : "No event has been recorded yet."}
            py="xl"
          />
        </Card>
      ) : (
        <TableCard title={openOnly ? "Open events" : "All events"}>
        <Table highlightOnHover verticalSpacing="sm" fz="sm" data-testid="events-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={150}>Time</Table.Th>
              <Table.Th w={110}>Severity</Table.Th>
              <Table.Th>Event</Table.Th>
              <Table.Th>Details</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {events.data?.items.map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td c="dimmed" style={{ whiteSpace: "nowrap" }}>
                  {formatLocal(e.t, tz)}
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <ThemeIcon size={22} radius="xl" variant="light" color={SEV[e.severity] ?? "gray"}>
                      {(() => {
                        const Ico = SEV_ICON[e.severity as keyof typeof SEV_ICON] ?? IconInfoCircle;
                        return <Ico size={14} />;
                      })()}
                    </ThemeIcon>
                    <Text size="xs" tt="capitalize">
                      {e.severity}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600} ff="monospace">
                    {e.kind}
                  </Text>
                  {EVENT_TEXT[e.kind] && (
                    <Text size="xs" c="dimmed">
                      {EVENT_TEXT[e.kind]}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <PayloadChips payload={e.payload as Record<string, unknown> | null | undefined} />
                </Table.Td>
                <Table.Td>
                  {METADATA_KINDS.has(e.kind) && typeof e.payload?.sha256 === "string" && (
                    <RequireRole min="admin">
                      <Button
                        size="compact-xs"
                        variant="light"
                        color="orange"
                        mr="xs"
                        onClick={() => acceptMetadata(e.payload?.sha256 as string, e.stacker_code)}
                      >
                        Accept metadata
                      </Button>
                    </RequireRole>
                  )}
                  {e.acknowledged_at ? (
                    <Text size="xs" c="dimmed">
                      acknowledged by {e.acknowledged_by}
                    </Text>
                  ) : (
                    <RequireRole min="operator">
                      <Button
                        size="compact-xs"
                        variant="light"
                        onClick={() =>
                          void act(() => ack.mutateAsync({ eventId: e.id }), {
                            done: `${e.kind} acknowledged`,
                            qc,
                            refresh: ["events"],
                          })
                        }
                      >
                        Acknowledge
                      </Button>
                    </RequireRole>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </TableCard>
      )}
    </Stack>
  );
}
